import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, batch } from 'react-redux';
import { setSolutionJson, setSolutionParam, setTourParameter, setError } from 'actions';
import { clearAll, integrateFileText } from 'utils/apiFilesUploadHelpers';
import { METHODS } from 'components/FloatingPanels/DevConsole/DevConsoleHeader';
import { parseSolutionJson } from 'utils/postMessageHelpers';

const WIZARD_INIT_SETTLE_MS = 150;

const usePostMessage = ({ user, oAuth, solution, translations, cookies }) => {
  const dispatch = useDispatch();

  const userRef = useRef(user);
  const oAuthRef = useRef(oAuth);
  const solutionShowRef = useRef(solution.show);
  const translationsRef = useRef(translations);
  const cookiesRef = useRef(cookies);
  const pendingMessageRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    oAuthRef.current = oAuth;
  }, [oAuth]);
  useEffect(() => {
    solutionShowRef.current = solution.show;
  }, [solution.show]);
  useEffect(() => {
    translationsRef.current = translations;
  }, [translations]);
  useEffect(() => {
    cookiesRef.current = cookies;
  }, [cookies]);

  const processSolutionMessage = useCallback(
    (json, filename) => {
      const currentTranslations = translationsRef.current;
      try {
        const { parsed, isUnassignedOnly } = parseSolutionJson(json);
        batch(() => {
          dispatch(setSolutionJson({ jsonNoTerritories: false, json: false }));
          dispatch(setTourParameter({ ordersMode: METHODS.JSON_IMPORT }));
          clearAll(dispatch, !userRef.current.hasStateMemory, false, solutionShowRef.current);
          dispatch(setSolutionParam({ param: { name: filename } }));
          if (isUnassignedOnly) {
            dispatch(
              setSolutionParam({
                param: { notices: parsed.notices || [], unassigned: parsed.unassigned },
              }),
            );
          }
          integrateFileText(
            json,
            'upload',
            filename,
            userRef.current,
            cookiesRef.current,
            oAuthRef.current,
            dispatch,
            true,
            currentTranslations.error,
            solutionShowRef.current,
            false,
            false,
            false,
            currentTranslations.map.depotMarkerTrans,
            currentTranslations.map.returnLocationMarkerTrans,
          );
        });
      } catch (err) {
        dispatch(setError(err));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (oAuth && pendingMessageRef.current) {
      const { json, filename } = pendingMessageRef.current;
      pendingMessageRef.current = null;
      setTimeout(() => processSolutionMessage(json, filename), WIZARD_INIT_SETTLE_MS);
    }
  }, [oAuth, processSolutionMessage]);

  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type !== 'load-solution' || !event.data?.solution) return;
      const json = JSON.stringify(event.data.solution, null, 2);
      const filename = event.data.filename || 'solution.json';
      pendingMessageRef.current = { json, filename };
      if (!oAuthRef.current) return;
      pendingMessageRef.current = null;
      setTimeout(() => processSolutionMessage(json, filename), WIZARD_INIT_SETTLE_MS);
    };
    window.addEventListener('message', handler);
    if (window.opener) {
      window.opener.postMessage({ type: 'avalon-ready' }, '*');
    }
    return () => window.removeEventListener('message', handler);
  }, [processSolutionMessage]);
};

export default usePostMessage;
